import React, { useState, useEffect } from 'react';
import { Search, Globe, MapPin, Calendar, TrendingUp, AlertCircle, ExternalLink, Sparkles } from 'lucide-react';
import * as i18nModule from '../services/i18n';
import apiRequest from '../services/api';

const t = i18nModule.t || ((key: string) => key);

interface SerpResult {
  position: number;
  title: string;
  link: string;
  domain: string;
  snippet: string;
  displayed_link?: string;
}

interface SerpSearchResponse {
  success: boolean;
  results: SerpResult[];
  totalResults?: number;
  searchMetadata?: {
    request_time_taken?: number;
  };
  error?: string;
}

// Helper function to get flag emoji for country
const getFlagEmoji = (country: string): string => {
  const flagMap: { [key: string]: string } = {
    'United States': '🇺🇸',
    'United Kingdom': '🇬🇧',
    'Canada': '🇨🇦',
    'Australia': '🇦🇺',
    'France': '🇫🇷',
    'Germany': '🇩🇪',
    'Spain': '🇪🇸',
    'Italy': '🇮🇹',
    'Netherlands': '🇳🇱',
    'Belgium': '🇧🇪',
    'Switzerland': '🇨🇭',
    'Morocco': '🇲🇦',
    'Algeria': '🇩🇿',
    'Tunisia': '🇹🇳',
    'Egypt': '🇪🇬',
    'Saudi Arabia': '🇸🇦',
    'United Arab Emirates': '🇦🇪',
    'India': '🇮🇳',
    'China': '🇨🇳',
    'Japan': '🇯🇵',
    'South Korea': '🇰🇷',
    'Brazil': '🇧🇷',
    'Mexico': '🇲🇽',
    'Argentina': '🇦🇷',
  };
  
  // Extract country from city,country format
  if (country.includes(',')) {
    const parts = country.split(',');
    const countryPart = parts[parts.length - 1].trim();
    return flagMap[countryPart] || '🌐';
  }
  
  return flagMap[country] || '🌐';
};

// Predefined locations list with flags
const PREDEFINED_LOCATIONS = [
  { value: 'United States', label: '🇺🇸 United States' },
  { value: 'United Kingdom', label: '🇬🇧 United Kingdom' },
  { value: 'Canada', label: '🇨🇦 Canada' },
  { value: 'Australia', label: '🇦🇺 Australia' },
  { value: 'France', label: '🇫🇷 France' },
  { value: 'Germany', label: '🇩🇪 Germany' },
  { value: 'Spain', label: '🇪🇸 Spain' },
  { value: 'Italy', label: '🇮🇹 Italy' },
  { value: 'Netherlands', label: '🇳🇱 Netherlands' },
  { value: 'Belgium', label: '🇧🇪 Belgium' },
  { value: 'Switzerland', label: '🇨🇭 Switzerland' },
  { value: 'Morocco', label: '🇲🇦 Morocco' },
  { value: 'Algeria', label: '🇩🇿 Algeria' },
  { value: 'Tunisia', label: '🇹🇳 Tunisia' },
  { value: 'Egypt', label: '🇪🇬 Egypt' },
  { value: 'Saudi Arabia', label: '🇸🇦 Saudi Arabia' },
  { value: 'United Arab Emirates', label: '🇦🇪 United Arab Emirates' },
  { value: 'India', label: '🇮🇳 India' },
  { value: 'China', label: '🇨🇳 China' },
  { value: 'Japan', label: '🇯🇵 Japan' },
  { value: 'South Korea', label: '🇰🇷 South Korea' },
  { value: 'Brazil', label: '🇧🇷 Brazil' },
  { value: 'Mexico', label: '🇲🇽 Mexico' },
  { value: 'Argentina', label: '🇦🇷 Argentina' },
  { value: 'New York,United States', label: '🇺🇸 New York, United States' },
  { value: 'Los Angeles,United States', label: '🇺🇸 Los Angeles, United States' },
  { value: 'London,United Kingdom', label: '🇬🇧 London, United Kingdom' },
  { value: 'Paris,France', label: '🇫🇷 Paris, France' },
  { value: 'Berlin,Germany', label: '🇩🇪 Berlin, Germany' },
  { value: 'Madrid,Spain', label: '🇪🇸 Madrid, Spain' },
  { value: 'Rome,Italy', label: '🇮🇹 Rome, Italy' },
  { value: 'Casablanca,Morocco', label: '🇲🇦 Casablanca, Morocco' },
  { value: 'Rabat,Morocco', label: '🇲🇦 Rabat, Morocco' },
];

// Language options with proper hl codes
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English', hl: 'en' },
  { value: 'fr', label: 'French', hl: 'fr' },
  { value: 'ar', label: 'Arabic', hl: 'ar' },
  { value: 'de', label: 'German', hl: 'de' },
  { value: 'es', label: 'Spanish', hl: 'es' },
  { value: 'it', label: 'Italian', hl: 'it' },
  { value: 'pt', label: 'Portuguese', hl: 'pt' },
  { value: 'nl', label: 'Dutch', hl: 'nl' },
  { value: 'ja', label: 'Japanese', hl: 'ja' },
  { value: 'zh', label: 'Chinese', hl: 'zh' },
  { value: 'ko', label: 'Korean', hl: 'ko' },
];

const SerpRankTracker: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [engine, setEngine] = useState<'google_rank_tracking' | 'google' | 'bing' | 'yahoo' | 'tiktok_ads_library' | 'meta_ad_library'>('google_rank_tracking');
  const [location, setLocation] = useState('United States');
  const [language, setLanguage] = useState('en');
  const [device, setDevice] = useState<'desktop' | 'mobile' | 'tablet'>('desktop');
  const [date, setDate] = useState<string>('');
  const [resultCount, setResultCount] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SerpResult[]>([]);
  const [error, setError] = useState<string>('');
  const [searchHistory, setSearchHistory] = useState<any[]>([]);
  const [remainingSearches, setRemainingSearches] = useState<number | null>(null);
  const [userPlan, setUserPlan] = useState<'free' | 'paid'>('free');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const resultsPerPage = 20; // Show 20 results per page

  useEffect(() => {
    fetchSearchHistory();
  }, []);

  const fetchSearchHistory = async () => {
    try {
      const response = await fetch('/api/serp/history', {
        credentials: 'include' as RequestCredentials, // Include cookies
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSearchHistory(data.history || []);
        setRemainingSearches(data.remainingSearches !== undefined ? data.remainingSearches : null);
        // Determine plan from maxSearches
        if (data.maxSearches) {
          setUserPlan(data.maxSearches >= 30 ? 'paid' : 'free');
        }
      }
    } catch (error) {
      console.error('Error fetching search history:', error);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!keyword.trim()) {
      setError('Please enter a keyword');
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);

    try {
      // Get hl code from language selection
      const selectedLang = LANGUAGE_OPTIONS.find(l => l.value === language);
      const hlCode = selectedLang?.hl || language;

      const data: SerpSearchResponse = await apiRequest<SerpSearchResponse>('/serp/search', {
        method: 'POST',
        body: JSON.stringify({
          keyword: keyword.trim(),
          engine,
          location,
          language: hlCode, // Send hl code instead of language value
          device,
          date: date || undefined,
          num: resultCount
        })
      });

      if (data.success) {
        setResults(data.results || []);
        setCurrentPage(1); // Reset to first page on new search
        // Refresh search history and remaining searches
        await fetchSearchHistory();
      } else {
        throw new Error(data.error || 'Search failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to perform search. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPositionColor = (position: number) => {
    if (position <= 3) return 'text-green-400';
    if (position <= 10) return 'text-blue-400';
    if (position <= 20) return 'text-yellow-400';
    return 'text-gray-400';
  };

  const getPositionBadgeColor = (position: number) => {
    if (position <= 3) return 'bg-green-500/20 border-green-500/50 text-green-300';
    if (position <= 10) return 'bg-blue-500/20 border-blue-500/50 text-blue-300';
    if (position <= 20) return 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300';
    return 'bg-gray-500/20 border-gray-500/50 text-gray-300';
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.6s ease-out forwards;
          opacity: 0;
        }
      `}</style>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            {(() => {
              const text = t('serp.title');
              return text && text !== 'serp.title' ? text : 'SERP Rank Tracker';
            })()}
          </h1>
          <p className="text-slate-400">
            {(() => {
              const text = t('serp.subtitle');
              return text && text !== 'serp.subtitle' ? text : 'See which websites rank in the top positions for any keyword';
            })()}
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-slate-800/50 rounded-xl p-6 mb-6 border border-slate-700">
          <form onSubmit={handleSearch} className="space-y-4">
            {/* Keyword Input */}
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                {(() => {
                  const text = t('serp.keyword');
                  return text && text !== 'serp.keyword' ? text : 'Keyword';
                })()}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder={(() => {
                    const text = t('serp.keywordPlaceholder');
                    return text && text !== 'serp.keywordPlaceholder' ? text : 'Enter keyword to track (e.g., "best SEO tools")';
                  })()}
                  required
                />
              </div>
            </div>

            {/* Search Options Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Engine Selection */}
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  <Globe className="inline mr-2" size={16} />
                  {(() => {
                    const text = t('serp.engine');
                    return text && text !== 'serp.engine' ? text : 'Search Engine';
                  })()}
                </label>
                <select
                  value={engine}
                  onChange={(e) => setEngine(e.target.value as any)}
                  className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="google_rank_tracking">🔍 Google (Rank Tracking)</option>
                  <option value="google">🔍 Google (Full)</option>
                  <option value="bing">🔎 Bing</option>
                  <option value="yahoo">🌐 Yahoo</option>
                  <option value="tiktok_ads_library">🎵 TikTok Ads Library</option>
                  <option value="meta_ad_library">📘 Meta Ads Library</option>
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  <MapPin className="inline mr-2" size={16} />
                  {(() => {
                    const text = t('serp.location');
                    return text && text !== 'serp.location' ? text : 'Location';
                  })()}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-lg pointer-events-none">
                    {getFlagEmoji(location)}
                  </span>
                  <select
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg pl-10 pr-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none cursor-pointer"
                  >
                    {PREDEFINED_LOCATIONS.map((loc) => (
                      <option key={loc.value} value={loc.value}>
                        {loc.label}
                      </option>
                    ))}
                  </select>
                  <Globe className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                </div>
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  {(() => {
                    const text = t('serp.language');
                    return text && text !== 'serp.language' ? text : 'Language';
                  })()}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-lg pointer-events-none">
                    {(() => {
                      const langFlags: { [key: string]: string } = {
                        'en': '🇺🇸',
                        'fr': '🇫🇷',
                        'ar': '🇸🇦',
                        'de': '🇩🇪',
                        'es': '🇪🇸',
                        'it': '🇮🇹',
                        'pt': '🇵🇹',
                        'nl': '🇳🇱',
                        'ja': '🇯🇵',
                        'zh': '🇨🇳',
                        'ko': '🇰🇷',
                      };
                      return langFlags[language] || '🌐';
                    })()}
                  </span>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg pl-10 pr-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none cursor-pointer"
                  >
                    {LANGUAGE_OPTIONS.map((lang) => {
                      const langFlags: { [key: string]: string } = {
                        'en': '🇺🇸',
                        'fr': '🇫🇷',
                        'ar': '🇸🇦',
                        'de': '🇩🇪',
                        'es': '🇪🇸',
                        'it': '🇮🇹',
                        'pt': '🇵🇹',
                        'nl': '🇳🇱',
                        'ja': '🇯🇵',
                        'zh': '🇨🇳',
                        'ko': '🇰🇷',
                      };
                      return (
                        <option key={lang.value} value={lang.value}>
                          {langFlags[lang.value] || '🌐'} {lang.label}
                        </option>
                      );
                    })}
                  </select>
                  <Globe className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                </div>
              </div>

              {/* Device */}
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  {(() => {
                    const text = t('serp.device');
                    return text && text !== 'serp.device' ? text : 'Device';
                  })()}
                </label>
                <select
                  value={device}
                  onChange={(e) => setDevice(e.target.value as any)}
                  className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="desktop">Desktop</option>
                  <option value="mobile">Mobile</option>
                  <option value="tablet">Tablet</option>
                </select>
              </div>
            </div>

            {/* Date and Result Count */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Date (Optional) */}
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  <Calendar className="inline mr-2" size={16} />
                  {(() => {
                    const text = t('serp.date');
                    return text && text !== 'serp.date' ? text : 'Date (Optional)';
                  })()}
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Result Count */}
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  <TrendingUp className="inline mr-2" size={16} />
                  {(() => {
                    const text = t('serp.resultCount');
                    return text && text !== 'serp.resultCount' ? text : 'Results to Show';
                  })()}
                </label>
                <select
                  value={resultCount}
                  onChange={(e) => setResultCount(Number(e.target.value))}
                  className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value={5}>Top 5</option>
                  <option value={10}>Top 10</option>
                  <option value={20}>Top 20</option>
                  <option value={50}>Top 50</option>
                  <option value={100}>Top 100</option>
                </select>
              </div>
            </div>

            {/* Search Button & Credits Info */}
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-slate-400">
                    {remainingSearches !== null && (
                      <span>
                        {(() => {
                          const text = t('serp.remainingSearches');
                          return text && text !== 'serp.remainingSearches' ? text : 'Remaining searches';
                        })()}: <span className="font-bold text-white">{remainingSearches}</span>
                        {' • '}
                        {(() => {
                          const text = t('serp.costPerSearch');
                          return text && text !== 'serp.costPerSearch' ? text : 'Cost';
                        })()}: <span className="font-bold text-indigo-400">3 credits</span>
                      </span>
                    )}
              </div>
              <button
                type="submit"
                disabled={loading || (remainingSearches !== null && remainingSearches <= 0)}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
              >
                {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {(() => {
                        const text = t('serp.searching');
                        return text && text !== 'serp.searching' ? text : 'Searching...';
                      })()}
                    </>
                  ) : (
                    <>
                      <Search size={20} />
                      {(() => {
                        const text = t('serp.search');
                        return text && text !== 'serp.search' ? text : 'Search Rankings';
                      })()}
                    </>
                  )}
              </button>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-300 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">{(() => {
                      const text = t('serp.error');
                      return text && text !== 'serp.error' ? text : 'Error';
                    })()}</p>
                    <p className="text-sm">{error}</p>
                  </div>
              </div>
            )}
          </form>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 animate-fadeIn">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Sparkles className="text-indigo-400" size={24} />
                <span>{(() => {
                  const text = t('serp.results');
                  return text && text !== 'serp.results' ? text : 'Search Results';
                })()}</span>
                <span className="text-indigo-400">- {results.length} {(() => {
                  const text = t('serp.results');
                  return text && text !== 'serp.results' ? text : 'Results';
                })()}</span>
              </h2>
              <div className="text-sm text-slate-400 flex items-center gap-2">
                <TrendingUp size={16} />
                <span>
                  {(() => {
                    const text = t('serp.showing');
                    return text && text !== 'serp.showing' ? text : 'Showing';
                  })()} {((currentPage - 1) * resultsPerPage) + 1}-{Math.min(currentPage * resultsPerPage, results.length)} {(() => {
                    const text = t('serp.of');
                    return text && text !== 'serp.of' ? text : 'of';
                  })()} {results.length}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {results.slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage).map((result, index) => (
                <div
                  key={result.position}
                  className="bg-gradient-to-r from-slate-900/80 to-slate-800/80 rounded-xl p-5 border border-slate-700 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/10 transition-all duration-300 animate-slideUp"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-start gap-4">
                    {/* Position Badge with Animation */}
                    <div className={`flex-shrink-0 w-14 h-14 rounded-xl border-2 flex items-center justify-center font-bold text-xl shadow-lg transition-transform hover:scale-110 ${getPositionBadgeColor(result.position)}`}>
                      #{result.position}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2 group">
                        <a
                          href={result.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-indigo-400 transition-colors flex items-center gap-2"
                        >
                          <span>{result.title}</span>
                          <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      </h3>
                      <p className="text-sm text-green-400 mb-2 font-medium flex items-center gap-1">
                        <MapPin size={12} />
                        {result.displayed_link || result.domain}
                      </p>
                      <p className="text-slate-300 text-sm leading-relaxed line-clamp-3">
                        {result.snippet}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {results.length > resultsPerPage && (
              <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
                >
                  {(() => {
                    const text = t('common.previous');
                    return text && text !== 'common.previous' ? text : 'Previous';
                  })()}
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.ceil(results.length / resultsPerPage) }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and pages around current
                    const totalPages = Math.ceil(results.length / resultsPerPage);
                    const showPage = 
                      page === 1 || 
                      page === totalPages || 
                      (page >= currentPage - 1 && page <= currentPage + 1) ||
                      (currentPage <= 3 && page <= 5) ||
                      (currentPage >= totalPages - 2 && page >= totalPages - 4);
                    
                    if (!showPage) {
                      // Show ellipsis
                      const prevPage = page - 1;
                      const nextPage = page + 1;
                      if (prevPage === 1 || nextPage === totalPages || 
                          (prevPage >= currentPage - 1 && prevPage <= currentPage + 1) ||
                          (nextPage >= currentPage - 1 && nextPage <= currentPage + 1)) {
                        return null;
                      }
                      return (
                        <span key={`ellipsis-${page}`} className="px-2 text-slate-500">...</span>
                      );
                    }
                    
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(results.length / resultsPerPage), prev + 1))}
                  disabled={currentPage >= Math.ceil(results.length / resultsPerPage)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
                >
                  {(() => {
                    const text = t('common.next');
                    return text && text !== 'common.next' ? text : 'Next';
                  })()}
                </button>
              </div>
            )}
          </div>
        )}

        {/* No Results Message */}
        {!loading && results.length === 0 && !error && (
          <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
            <Search size={48} className="mx-auto text-slate-500 mb-4" />
            <p className="text-slate-400 text-lg">
              {(() => {
                const text = t('serp.noResults');
                return text && text !== 'serp.noResults' ? text : 'Enter a keyword and click "Search Rankings" to see which websites rank in the top positions.';
              })()}
            </p>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default SerpRankTracker;

